import React, { useEffect, useState } from "react";
import { useMap } from "react-leaflet";

// Route helpers so maritime routes stay continuous across the antimeridian and
// repeat on every world copy Leaflet tiles horizontally.

// GeoJSON geometries / Features (LineString or MultiLineString) -> list of [lng, lat] lines
export const geoJsonToLines = (geos = []) =>
  geos.flatMap((g) => {
    const geom = g?.geometry || g;
    if (!geom?.coordinates) return [];
    return geom.type === "MultiLineString" ? geom.coordinates : [geom.coordinates];
  });

// Unwrap [lng, lat] lines into one continuous track: a leg crossing 180° goes 170 -> 190, not 170 -> -170
export const unwrapLines = (lines = []) => {
  let prev = null;
  return lines
    .filter((line) => line?.length)
    .map((line) =>
      line.map(([lng, lat]) => {
        lng = Number(lng);
        if (prev !== null) lng += Math.round((prev - lng) / 360) * 360;
        prev = lng;
        return [lng, Number(lat)];
      })
    );
};

// Shift a lng by a multiple of 360 so the point sits on the unwrapped route
export const alignLng = (lng, lines = []) => {
  lng = Number(lng) || 0;
  let best = lng;
  let bestD = Infinity;
  for (const line of lines) {
    for (const [x] of line) {
      const cand = lng + Math.round((x - lng) / 360) * 360;
      const d = Math.abs(x - cand);
      if (d < bestD) [bestD, best] = [d, cand];
    }
  }
  return best;
};

// Offsets (multiples of 360) of every world copy in view that could show something spanning [minLng, maxLng]
export const visibleWorldOffsets = (map, minLng, maxLng) => {
  const offsets = [0];
  if (!isFinite(minLng) || !isFinite(maxLng)) return offsets;
  const view = map.getBounds();
  const from = Math.ceil((view.getWest() - maxLng) / 360);
  const to = Math.floor((view.getEast() - minLng) / 360);
  for (let k = from; k <= to; k++) if (k !== 0) offsets.push(k * 360);
  return offsets;
};

// Plain Leaflet: calls drawCopy(offset) once per world copy as it scrolls into view. Returns a detach fn.
export const repeatOnWorldCopies = (map, lngs, drawCopy) => {
  const finite = lngs.filter(isFinite);
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const drawn = new Set();
  const sync = () =>
    visibleWorldOffsets(map, min, max).forEach((offset) => {
      if (drawn.has(offset)) return;
      drawn.add(offset);
      drawCopy(offset);
    });
  sync();
  map.on("moveend", sync);
  return () => map.off("moveend", sync);
};

// react-leaflet: renders children(offset) for each world copy in view
export const WorldCopies = ({ lngs = [], children }) => {
  const map = useMap();
  const finite = lngs.filter(isFinite);
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const [offsets, setOffsets] = useState([0]);

  useEffect(() => {
    const sync = () =>
      setOffsets((prev) => {
        const next = visibleWorldOffsets(map, min, max);
        return next.join() === prev.join() ? prev : next;
      });
    sync();
    map.on("moveend", sync);
    return () => map.off("moveend", sync);
  }, [map, min, max]);

  return offsets.map((offset) => <React.Fragment key={offset}>{children(offset)}</React.Fragment>);
};
